"use client";

import { ArrowDownCircle, ArrowLeft, ArrowUpCircle, Coins } from "lucide-react";
import React from "react";
import dayjs from "@punchup/utils/dayjs";

import { Button } from "../../../shadcn/ui/button";
import { Skeleton } from "../../../shadcn/ui/skeleton";
import { ConfirmationDialog } from "../../common/ConfirmationDialog";
import { cn } from "@punchup/utils/cn";
import type { TierCard } from "./types";

export type TierChangeDirection = "upgrade" | "downgrade";
export type TierChangePreviewStatus = "idle" | "loading" | "ready" | "error";

function capitalize(tier: string): string {
  return tier.charAt(0).toUpperCase() + tier.slice(1);
}

export interface GetTierChangeSummaryArgs {
  /** Generation-correct tier cards (the subscriber's OWN generation — D10). */
  cards: TierCard[];
  direction: TierChangeDirection;
  renewalDate: string | null;
  /** Exact previewed charge in cents for an upgrade. Omit to render the "at least" range fallback. */
  previewedAmountCents?: number;
}

export interface TierChangeSummary {
  title: string;
  /** Credits chip text — upgrade only, null for downgrade. */
  creditsChipText: string | null;
  chargeLine: string;
  renewLine: string;
  confirmLabel: string;
  cancelLabel: string;
}

/** Pure summary builder — every figure derives from `cards`, never a hardcoded dollar amount. */
export function getTierChangeSummary({
  cards,
  direction,
  renewalDate,
  previewedAmountCents,
}: GetTierChangeSummaryArgs): TierChangeSummary {
  const starterCard = cards.find((c) => c.tier === "starter")!;
  const proCard = cards.find((c) => c.tier === "pro")!;
  const newTierCard = direction === "upgrade" ? proCard : starterCard;
  const oldTierCard = direction === "upgrade" ? starterCard : proCard;
  const newTierName = capitalize(newTierCard.tier);
  const cancelLabel = "Keep current plan";

  if (direction === "upgrade") {
    const minChargeToday = newTierCard.amount - oldTierCard.amount;
    const chargeLine =
      previewedAmountCents != null
        ? `You'll be charged USD ${(previewedAmountCents / 100).toFixed(2)} today — the prorated cost of your new ${newTierName} cycle after credit for unused time on your current plan.`
        : `USD ${newTierCard.amount}.00 for your new ${newTierName} cycle, minus credit for unused time on your current plan (at least USD ${minChargeToday}.00 today).`;

    return {
      title: `Upgrade to ${newTierName}?`,
      creditsChipText: `${newTierCard.credits} added instantly`,
      chargeLine,
      renewLine: `Your subscription renews at USD ${newTierCard.amount}.00/mo starting today.`,
      confirmLabel: "Confirm upgrade",
      cancelLabel,
    };
  }

  const renewalText = renewalDate
    ? dayjs(renewalDate).format("MMM D, YYYY")
    : "your next renewal";

  return {
    title: `Switch to ${newTierName}?`,
    creditsChipText: null,
    chargeLine:
      "No charge today — your credits and current billing cycle are unchanged.",
    renewLine: `Starting ${renewalText}, you'll be billed USD ${newTierCard.amount}.00/mo and receive ${newTierCard.credits} each month.`,
    confirmLabel: "Confirm downgrade",
    cancelLabel,
  };
}

function DirectionIconCircle({
  direction,
  size,
}: {
  direction: TierChangeDirection;
  size: "sm" | "md";
}) {
  const Icon = direction === "upgrade" ? ArrowUpCircle : ArrowDownCircle;
  return (
    <div
      className={cn(
        "flex shrink-0 items-center justify-center rounded-full bg-[#F2F2F3] text-secondary-blue",
        size === "md" ? "h-[42px] w-[42px]" : "h-[34px] w-[34px]"
      )}
    >
      <Icon className={size === "md" ? "h-6 w-6" : "h-5 w-5"} />
    </div>
  );
}

function CreditsChip({ text }: { text: string }) {
  return (
    <div className="inline-flex w-fit items-center gap-1.5 rounded-full bg-[#F2F2F3] px-2.5 py-1 text-xs font-semibold text-secondary-blue">
      <Coins className="h-3.5 w-3.5" />
      {text}
    </div>
  );
}

function ChargeLine({
  direction,
  chargeLine,
  previewStatus,
}: {
  direction: TierChangeDirection;
  chargeLine: string;
  previewStatus: TierChangePreviewStatus;
}) {
  if (direction === "upgrade" && previewStatus === "loading") {
    return <Skeleton className="h-4 w-3/4" aria-hidden />;
  }
  return <p className="text-sm text-primary-blue">{chargeLine}</p>;
}

export interface TierChangeConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  direction: TierChangeDirection;
  /** Generation-correct tier cards (the subscriber's own generation). */
  cards: TierCard[];
  renewalDate: string | null;
  onConfirm: () => void;
  /** Upgrade-only exact proration preview. */
  previewedAmountCents?: number;
  previewStatus?: TierChangePreviewStatus;
}

/** Desktop confirmation dialog — a thin composition over the generalized `ConfirmationDialog`. */
export function TierChangeConfirmDialog({
  open,
  onOpenChange,
  direction,
  cards,
  renewalDate,
  onConfirm,
  previewedAmountCents,
  previewStatus = "idle",
}: TierChangeConfirmDialogProps) {
  const summary = getTierChangeSummary({
    cards,
    direction,
    renewalDate,
    previewedAmountCents:
      previewStatus === "ready" ? previewedAmountCents : undefined,
  });

  return (
    <ConfirmationDialog
      open={open}
      onOpenChange={onOpenChange}
      variant="default"
      confirmVariant="punchupSolid"
      title={summary.title}
      headerIcon={<DirectionIconCircle direction={direction} size="md" />}
      description={
        <div className="space-y-2">
          {summary.creditsChipText && (
            <CreditsChip text={summary.creditsChipText} />
          )}
          <ChargeLine
            direction={direction}
            chargeLine={summary.chargeLine}
            previewStatus={previewStatus}
          />
          <p className="text-xs text-secondary-blue">{summary.renewLine}</p>
        </div>
      }
      confirmLabel={summary.confirmLabel}
      cancelLabel={summary.cancelLabel}
      onConfirm={onConfirm}
    />
  );
}

export interface MobileTierChangeConfirmScreenProps {
  direction: TierChangeDirection;
  cards: TierCard[];
  renewalDate: string | null;
  onBack: () => void;
  onConfirm: () => void;
  previewedAmountCents?: number;
  previewStatus?: TierChangePreviewStatus;
}

/** Mobile full-screen panel — structural clone of `MobileCancelConfirmScreen` (the drawer z-index constraint rules out an AlertDialog here). */
export function MobileTierChangeConfirmScreen({
  direction,
  cards,
  renewalDate,
  onBack,
  onConfirm,
  previewedAmountCents,
  previewStatus = "idle",
}: MobileTierChangeConfirmScreenProps) {
  const summary = getTierChangeSummary({
    cards,
    direction,
    renewalDate,
    previewedAmountCents:
      previewStatus === "ready" ? previewedAmountCents : undefined,
  });

  return (
    <div className="flex flex-col h-full px-6 py-6">
      <button
        type="button"
        onClick={onBack}
        className="flex items-center gap-1 text-sm text-secondary-blue mb-6"
      >
        <ArrowLeft className="h-4 w-4" />
        Back
      </button>
      <div className="flex items-center gap-3 mb-3">
        <DirectionIconCircle direction={direction} size="sm" />
        <h3 className="text-lg font-semibold text-primary-blue">
          {summary.title}
        </h3>
      </div>
      {summary.creditsChipText && (
        <div className="mb-2">
          <CreditsChip text={summary.creditsChipText} />
        </div>
      )}
      <div className="mt-1">
        <ChargeLine
          direction={direction}
          chargeLine={summary.chargeLine}
          previewStatus={previewStatus}
        />
      </div>
      <p className="mt-1 text-xs text-secondary-blue">{summary.renewLine}</p>
      <div className="flex flex-col gap-3 mt-auto">
        <Button variant="punchupSolid" className="w-full" onClick={onConfirm}>
          {summary.confirmLabel}
        </Button>
        <Button variant="outline" className="w-full" onClick={onBack}>
          {summary.cancelLabel}
        </Button>
      </div>
    </div>
  );
}
